import { lazy, memo, Suspense, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { selectQuickOrderData } from '../../selectors/orders'

const loadQuickTradeWindow = () => import('./QuickTradeWindowV2')
const QuickTradeWindowV2 = lazy(loadQuickTradeWindow)

/**
 * Keeps the quick trade window (~170 KB minified with its dialogs) out of the entry bundle.
 * The chunk is prefetched once the page has loaded and gone idle, so it neither competes with the
 * chart's first load nor makes the first order wait on the network. The window is mounted the
 * first time it opens and kept mounted afterwards, as before.
 */
const QuickTradeWindowLoader = () => {
    const quickOrder = useSelector(selectQuickOrderData)
    const isVisible = !!quickOrder?.openDialog && (quickOrder.type === 'trade' || quickOrder.type === 'modify')
    const mountedRef = useRef(false)
    if (isVisible) {
        mountedRef.current = true
    }

    useEffect(() => {
        let cancelled = false
        const prefetch = () => {
            if (!cancelled) void loadQuickTradeWindow()
        }
        const scheduleIdle = () => {
            if (typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(prefetch, { timeout: 5000 })
            } else {
                setTimeout(prefetch, 2000)
            }
        }
        if (document.readyState === 'complete') {
            scheduleIdle()
        } else {
            window.addEventListener('load', scheduleIdle, { once: true })
        }
        return () => {
            cancelled = true
            window.removeEventListener('load', scheduleIdle)
        }
    }, [])

    if (!mountedRef.current) return null
    return (
        <Suspense fallback={null}>
            <QuickTradeWindowV2 />
        </Suspense>
    )
}

export default memo(QuickTradeWindowLoader)
